// React and Redux related imports
import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// Material-UI components
import { Typography } from '@mui/material';

// Local components and styles
import { DrawerContentContainer } from '../../AdminDrawer/AdminDrawer.styles';
import { SpinnerContainer } from '../../../pages/Dashboard/Dashboard.styles';
import { TempoLogo } from '../ShiftStatus/ShiftStatus.styles';
import DailyFinancialMetrics from '../../DailyFinancialMetrics';
import Notes from '../../Notes/Notes';
import { useShiftNotes } from '../../../utils/Hooks/shifts/useShiftNotes';

// Redux selectors and thunks
import { selectDailyTicketsStatus } from '../../../redux/dailyTickets/dailyTicketsSlice';
import {
  selectCurrentVenue,
  selectHasPrivilegedRole,
  selectCurrentUserId,
} from '../../../redux/slices/currentUserSlice';
import { fetchDailyTicketByDate } from '../../../redux/dailyTickets/dailyTicketsThunks';
import { fetchVenueShifts } from '../../../redux/shifts/shiftsThunks';
import { selectShiftStatus, selectAllShifts } from '../../../redux/shifts/shiftsSlice';
import {
  selectSelectedDailyTicket,
  startDailyTicketListener,
  stopDailyTicketListener,
} from '../../../redux/dailyTickets/dailyTicketsSlice';

// Assets and utilities
import Spinner from '../../../assets/svg/spinner';
import circleBlurred from '../../../assets/logo/circleBlurred.png';
import dayjs from '../../../utils/dayjsConfig';
import { LOADING_STATES } from '../../../utils/constants/loadingStates';
import DailyNotes from './Components/DailyNotes';
import DailyShiftsList from './Components/DailyShiftsList';
import DailyShiftsActionButtons from './Components/DailyShiftsActionButtons';
import { getVenueTimezone } from '../../../utils/venueTimeFormatters';

/**
 * DailyShiftSummaryPanel Component
 *
 * Displays a summary panel for daily shifts. The content varies based on user role:
 * - For privileged users (managers/owners): Shows financial metrics, labor cost analysis, and shift management options
 * - For employees: Shows all shifts for the day and shift notes
 *
 * @param {Object} props - Component props
 * @param {Array} props.shifts - Array of shift objects for the selected day
 * @param {string} props.selectedDay - The selected day in ISO format
 * @param {Function} props.handleOpenDrawer - Function to open drawers
 * @returns {JSX.Element} The rendered component
 */
const DailyShiftSummaryPanel = ({ shifts: initialShifts, selectedDay, handleOpenDrawer }) => {
  // Redux hooks
  const dispatch = useDispatch();
  const liveTicket = useSelector(selectSelectedDailyTicket);
  const dailyTicketsStatus = useSelector(selectDailyTicketsStatus);
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);
  const currentVenue = useSelector(selectCurrentVenue);
  const venueId = currentVenue.id;
  const currentUserId = useSelector(selectCurrentUserId);
  const shiftStatus = useSelector(selectShiftStatus);
  const allShifts = useSelector(selectAllShifts);

  // Get shifts for the current day from Redux store
  const currentDayShifts = useMemo(() => {
    if (!selectedDay || !allShifts?.length) return [];
    const currentDate = dayjs(selectedDay).format('YYYY-MM-DD');
    return allShifts.filter((shift) => dayjs(shift.start).format('YYYY-MM-DD') === currentDate);
  }, [selectedDay, allShifts]);

  // Construct the Document ID for the daily ticket
  const dateString = useMemo(() => {
    if (!selectedDay || !currentVenue) return null;
    const venueTimezone = getVenueTimezone(currentVenue);
    const venueDate = dayjs.tz(selectedDay, venueTimezone);
    return venueDate.startOf('day').format('YYYY-MM-DD');
  }, [selectedDay, currentVenue]);

  const documentId = useMemo(() => {
    if (!dateString || !venueId) return null;
    return `${venueId}_${dateString}`;
  }, [venueId, dateString]);

  // Use either Redux shifts or props shifts
  const shifts = currentDayShifts.length > 0 ? currentDayShifts : initialShifts;

  // For employee view, find their shift for the day
  const employeeShift = useMemo(() => {
    if (!hasPrivilegedRole && currentUserId) {
      return shifts.find(
        (shift) =>
          shift.assignedId === currentUserId ||
          shift.employeeId === currentUserId ||
          shift.assignedEmployeeId === currentUserId
      );
    }
    return null;
  }, [shifts, currentUserId, hasPrivilegedRole]);

  // Use shift notes hook for the employee's shift
  const { updatedNotes, handleSaveNote } = useShiftNotes({
    shiftID: employeeShift?.id,
    shift: employeeShift,
  });

  // Derived state
  const isLoading =
    dailyTicketsStatus === LOADING_STATES.LOADING || (shiftStatus === LOADING_STATES.LOADING && !shifts?.length);

  // Effects
  useEffect(() => {
    if (venueId && dateString && documentId) {
      // Fetch daily ticket by document ID
      dispatch(fetchDailyTicketByDate({ vid: venueId, dateString }));

      // Start real-time listener for the specific document by ID
      dispatch(startDailyTicketListener(documentId));

      // Fetch shifts for the period if they're not loaded
      if (!shifts?.length) {
        const venueTimezone = getVenueTimezone(currentVenue);
        const venueDate = dayjs.tz(selectedDay, venueTimezone);
        const period = {
          start: venueDate.startOf('day').toISOString(),
          end: venueDate.endOf('day').toISOString(),
        };
        dispatch(fetchVenueShifts({ venueId, period }));
      }

      // Cleanup listener when component unmounts or when date/venue changes
      return () => {
        dispatch(stopDailyTicketListener());
      };
    }
  }, [dispatch, venueId, dateString, documentId, shifts?.length, selectedDay, currentVenue]);

  if (isLoading) {
    return (
      <DrawerContentContainer>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </DrawerContentContainer>
    );
  }

  // Main render
  return (
    <DrawerContentContainer>
      <TempoLogo src={circleBlurred} style={{ marginTop: '-50px', marginBottom: '-50px' }} alt="Tempo Logo" />

      {hasPrivilegedRole ? (
        // Manager/Owner View
        <>
          {liveTicket ? (
            <>
              <Typography variant="h6">{liveTicket.laborCostAnalysis || 'No labor cost analysis available'}</Typography>
              <DailyFinancialMetrics shifts={shifts} liveTicket={liveTicket} />
            </>
          ) : (
            <Typography variant="h6">No financial data available for this day</Typography>
          )}

          <DailyNotes selectedDay={selectedDay} liveTicket={liveTicket} />
          <DailyShiftsList
            shifts={shifts}
            selectedDay={selectedDay}
            handleOpenDrawer={handleOpenDrawer}
            venueId={venueId}
          />
          <DailyShiftsActionButtons selectedDay={selectedDay} shifts={shifts} handleOpenDrawer={handleOpenDrawer} />
        </>
      ) : (
        // Employee View - Show all shifts and personal notes
        <>
          <Notes
            title="Shift Notes"
            placeholder={employeeShift ? 'Add notes visible to everyone' : 'No shift notes for this day'}
            disabled={!employeeShift}
            value={updatedNotes}
            onChange={handleSaveNote}
          />
          <DailyShiftsList
            shifts={shifts}
            selectedDay={selectedDay}
            handleOpenDrawer={handleOpenDrawer}
            venueId={venueId}
            isEmployeeView={true}
            currentUserId={currentUserId}
          />
        </>
      )}
    </DrawerContentContainer>
  );
};

DailyShiftSummaryPanel.propTypes = {
  shifts: PropTypes.array.isRequired,
  selectedDay: PropTypes.string,
  handleOpenDrawer: PropTypes.func.isRequired,
};

export default DailyShiftSummaryPanel;
